import { useTranslation } from 'react-i18next';
import { useEulaProfileForm } from '../model/useEulaProfileForm';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Button,
  Loader,
  ErrorText,
  ModalComponentWrapper,
  FormFieldCheckbox,
  MarkdownText,
  DatePickerInput,
} from '@/shared/components';
import { CountrySelectInput } from '@/shared/components';
import { useCountriesDictionary } from '@/shared/api/queries';
import { type EulaProfileFormValues } from '../model/validation/eulaProfileSchema';
import { useVerificationEulaCheckBoxes } from '../api/queries/useVerificationEulaCheckBoxes';
import { cn } from '@/shared/utils/styling';
import type { FieldErrors } from 'react-hook-form';
import { useVerificationProfileSubmit } from '../api/queries/useVerificationProfileSubmit';
import { DATE_FORMAT_FULL, VERIFICATION_EULA_ENABLED } from '@/shared/config';
import { getRequiredLabel, useDateModal } from '@/shared/lib';
import { useVerificationModal } from '@/features/user-verification/model';
import { format, isValid, parse } from 'date-fns';

type Props = {
  goBack?: () => void;
  initialValues?: EulaProfileFormValues;
};

const hasCheckBoxesResult = (
  errors: FieldErrors<EulaProfileFormValues>
): errors is FieldErrors<EulaProfileFormValues> & {
  checkBoxesResult?: Array<{ isSelected?: { message?: string } }>;
} => {
  return 'checkBoxesResult' in errors;
};

const VerificationEulaProfileForm = ({ goBack, initialValues }: Props) => {
  const { t } = useTranslation();
  const { checkBoxes, isEulaCheckBoxesLoading } =
    useVerificationEulaCheckBoxes();
  const { countriesData, isCountriesLoading } = useCountriesDictionary();

  const { form } = useEulaProfileForm(
    !VERIFICATION_EULA_ENABLED,
    initialValues
  );

  const { openVerificationModal } = useVerificationModal();
  const { openDateModal } = useDateModal();

  const handleDateModalOpen = () => {
    const parsedDate = parse(
      form.getValues('dateOfBirth'),
      DATE_FORMAT_FULL,
      new Date()
    );

    openDateModal({
      onApply: (date?: Date) => {
        openVerificationModal({
          ...form.getValues(),
          dateOfBirth: date ? format(date!, DATE_FORMAT_FULL) : '',
        });
      },
      onBack: () =>
        openVerificationModal({
          ...form.getValues(),
        }),
      isSingle: true,
      initDate: isValid(parsedDate) ? parsedDate : undefined,
    });
  };

  const {
    submitVerificationProfile,
    isSubmitVerificationProfileLoading,
    submitVerificationProfileError,
  } = useVerificationProfileSubmit();

  const onSubmit = (data: EulaProfileFormValues) => {
    const formattedDateOfBirth = data.dateOfBirth.replaceAll('.', '');
    submitVerificationProfile({ ...data, dateOfBirth: formattedDateOfBirth });
  };

  return (
    <ModalComponentWrapper
      title={t('personalAccount.verification.title')}
      description={t(
        'personalAccount.verification.eulaProfileForm.description'
      )}
      onBack={goBack}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-[2.4rem]">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    label={getRequiredLabel(
                      t(
                        'personalAccount.verification.eulaProfileForm.firstNamePlaceholder'
                      )
                    )}
                    className="p3"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    label={getRequiredLabel(
                      t(
                        'personalAccount.verification.eulaProfileForm.lastNamePlaceholder'
                      )
                    )}
                    className="p3"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="middleName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    label={t(
                      'personalAccount.verification.eulaProfileForm.middleNamePlaceholder'
                    )}
                    className="p3"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dateOfBirth"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormControl>
                  <DatePickerInput
                    {...field}
                    handleDateModalOpen={handleDateModalOpen}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="citizenship"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  {isCountriesLoading ? (
                    <Loader />
                  ) : (
                    <CountrySelectInput
                      side="top"
                      value={field.value}
                      onValueChange={field.onChange}
                      countries={countriesData?.value || []}
                      placeholder={getRequiredLabel(
                        t(
                          'personalAccount.verification.eulaProfileForm.citizenshipPlaceholder',
                          'Select Country'
                        )
                      )}
                      error={form.formState.errors.citizenship}
                      buttonClassName={cn(
                        'bg-muted dark:bg-muted border-none dark:aria-invalid:bg-light-error',
                        'dark:aria-invalid:text-primary',
                        !field.value && 'text-muted-foreground'
                      )}
                    />
                  )}
                </FormControl>
              </FormItem>
            )}
          />

          {VERIFICATION_EULA_ENABLED && isEulaCheckBoxesLoading ? (
            <Loader />
          ) : (
            checkBoxes &&
            checkBoxes.map((checkbox, index) => {
              const errors = form.formState.errors;
              const hasError = hasCheckBoxesResult(errors)
                ? errors.checkBoxesResult?.[index]?.isSelected
                : undefined;
              return (
                <FormFieldCheckbox
                  key={checkbox.checkBoxId}
                  name={`checkBoxesResult.${index}.isSelected`}
                  label={
                    <MarkdownText variant="eula" isError={!!hasError}>
                      {checkbox.questionValue}
                    </MarkdownText>
                  }
                  hasError={!!hasError}
                />
              );
            })
          )}

          {submitVerificationProfileError && (
            <ErrorText>{submitVerificationProfileError?.message}</ErrorText>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            loading={isSubmitVerificationProfileLoading}
            disabled={isSubmitVerificationProfileLoading}>
            {t('personalAccount.verification.eulaProfileForm.submitButton')}
          </Button>
        </form>
      </Form>
    </ModalComponentWrapper>
  );
};

export { VerificationEulaProfileForm };
